import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lightbulb, FileInput, LayoutGrid, ClipboardCheck, History, Sparkles } from 'lucide-react';
import api from '../utils/api';

function greetingWord() {
  const h = new Date().getHours();
  if (h < 5) return '夜深了';
  if (h < 12) return '早上好';
  if (h < 18) return '下午好';
  return '晚上好';
}

// 三扇独立的门：跟"我有一个想法"输入框分开，各自清楚
const DOORS = [
  { label: '导入内容', desc: '剧本或小说 → 改编成短剧', icon: FileInput,      to: '/writing' },
  { label: '无限画布', desc: '给剧本做 AI 视频',        icon: LayoutGrid,     to: '/canvas' },
  { label: '剧本审核', desc: '给剧本打分、看质量',      icon: ClipboardCheck, to: '/writing?tab=review' },
];

export default function Home() {
  const [user, setUser] = useState(null);
  const [recent, setRecent] = useState(null);
  const [idea, setIdea] = useState('');
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/users/me').then(r => setUser(r.data)).catch(() => {});
    api.get('/agent/projects')
      .then(r => setRecent(Array.isArray(r.data) ? r.data[0] : r.data?.projects?.[0] || null))
      .catch(() => {});
  }, []);

  // 首页直达：建项目 → 进工作台并自动把想法发给小影（落地即见 AI 回应）
  const start = async () => {
    const t = idea.trim();
    if (!t) { navigate('/writing'); return; }
    if (creating) return;
    setCreating(true);
    try {
      const title = t.length > 18 ? `${t.slice(0, 18)}…` : t;
      const r = await api.post('/agent/projects', { title });
      navigate(`/writing?open=${r.data.id}&send=${encodeURIComponent(t)}`);
    } catch {
      navigate(`/writing?idea=${encodeURIComponent(t)}`); // 建项目失败则降级到卡片墙
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f6f3ec] dark:bg-[#0e0e10]">
      {/* 顶部：点数 + 升级 */}
      <div className="flex shrink-0 items-center justify-end gap-3 px-6 py-4">
        <span className="flex items-center gap-1.5 text-[12px] text-stone-500 dark:text-gray-400">
          <Sparkles size={14} className="text-[#6f6196] dark:text-[#b9afe7]" />
          {user?.credits ?? 0} 点
        </span>
        <button
          onClick={() => navigate('/profile')}
          className="rounded-lg bg-[#eee9de] px-3 py-1.5 text-[12px] font-medium text-[#6f6196] transition-colors hover:bg-[#e5dece] dark:bg-white/[0.08] dark:text-[#b9afe7]"
        >
          升级
        </button>
      </div>

      {/* 中间：问候 + 原创输入框 + 三扇门 */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 pb-12">
        <h1 className="mb-1 text-[25px] font-semibold tracking-tight text-stone-900 dark:text-gray-100">
          {greetingWord()}，{user?.username || '创作者'}
        </h1>
        <p className="mb-6 text-[14px] text-stone-400 dark:text-gray-500">有个想法就说，小影陪你一起写成短剧</p>

        {/* 我有一个想法 —— 原创专用输入框 */}
        <div className="w-full max-w-[580px]">
          <div className="mb-2 flex items-center gap-1.5 text-[13px] font-medium text-[#6f6196] dark:text-[#b9afe7]">
            <Lightbulb size={16} />我有一个想法
          </div>
          <div className="rounded-2xl border border-[#c9bedc] bg-white p-4 shadow-lg shadow-stone-300/40 dark:border-white/[0.12] dark:bg-[#14141a] dark:shadow-black/30">
            <textarea
              ref={inputRef}
              value={idea}
              onChange={e => setIdea(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); start(); } }}
              rows={3}
              placeholder="说个短剧故事，比如：豪门替嫁的新娘，暗中查亡姐死因，一步步反杀真凶…"
              className="w-full resize-none bg-transparent text-[14px] leading-relaxed text-stone-800 outline-none placeholder:text-stone-400 dark:text-gray-200"
            />
            <div className="mt-1.5 flex items-center justify-end">
              <button
                onClick={start}
                disabled={creating}
                className="flex items-center gap-1.5 rounded-xl bg-[#8c7db0] px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-[#7d6ca3] disabled:opacity-60"
              >
                {creating ? '创建中…' : <>开始创作<ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        </div>

        {/* 或者 */}
        <div className="my-5 flex w-full max-w-[580px] items-center gap-3">
          <div className="h-px flex-1 bg-[#ded6c8] dark:bg-white/[0.08]" />
          <span className="text-[11px] text-stone-400">或者</span>
          <div className="h-px flex-1 bg-[#ded6c8] dark:bg-white/[0.08]" />
        </div>

        {/* 三扇独立的门 */}
        <div className="grid w-full max-w-[580px] grid-cols-3 gap-3">
          {DOORS.map(door => {
            const Icon = door.icon;
            return (
              <button
                key={door.label}
                onClick={() => navigate(door.to)}
                className="flex flex-col items-start gap-1.5 rounded-xl border border-[#ded6c8] bg-white p-3.5 text-left transition-colors hover:border-[#c9bedc] dark:border-white/[0.06] dark:bg-[#14141a]"
              >
                <Icon size={20} className="text-[#6f6196] dark:text-[#b9afe7]" />
                <span className="text-[13px] font-medium text-stone-900 dark:text-gray-100">{door.label}</span>
                <span className="text-[11px] leading-snug text-stone-400 dark:text-gray-500">{door.desc}</span>
              </button>
            );
          })}
        </div>

        {/* 继续最近 */}
        {recent?.id && (
          <button
            onClick={() => navigate('/writing')}
            className="mt-6 flex items-center gap-2 text-[12px] text-stone-400 transition-colors hover:text-stone-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <History size={14} />
            继续最近：<span className="text-stone-600 dark:text-gray-300">{recent.title}</span>
          </button>
        )}
      </div>
    </div>
  );
}
